import React, { useMemo } from "react";

import { useValueChain } from "../../hooks/queries/useValueChain";
import type { EntityDetailProps } from "../../types/entityRegistry";
import CategoryDetailView from "../CategoryDetail/CategoryDetailView";
import { HeaderExtraActionsContext } from "../Layout/EntityDetailOverlay/EntityFunctionalHeader";
import { ChainCardRef } from "../ValueChain/valueChainState";
import ChainPlace from "./ChainPlace";
import { makeRootAction } from "./MakeRootButton";
import { chainSections, facilitiesSection } from "./chainSections";
import { neighbourParams, useMakeRoot, usePageChain } from "./kindChain";

/**
 * The detail of a class of the value chain: the registry's Detail component
 * of the type.
 *
 * It is the category view of the class, with "make root" in its header,
 * where the class sits in the chain, and the chain sections: its makers, the
 * kinds it is built into, the kinds of part stated inside it and the
 * production sites of its makers, from the value chain read one step to each
 * side with the class as root.
 */
const ClassDetailView: React.FC<EntityDetailProps> = (props) => {
  const { entityId } = props;
  const { data: chain } = useValueChain(
    entityId ? neighbourParams("class", entityId) : null,
  );
  const page = usePageChain();
  const makeRoot = useMakeRoot();

  const root = useMemo<ChainCardRef>(
    () => ({ type: "class", id: entityId }),
    [entityId],
  );

  const extraSections = useMemo(
    () => [
      ...chainSections("class", chain, page?.chain, entityId, makeRoot),
      facilitiesSection(chain),
    ],
    [chain, page, entityId, makeRoot],
  );

  // The category view renders the header: "make root" reaches it as the
  // extra actions of a view built on it
  return (
    <HeaderExtraActionsContext.Provider value={makeRootAction("class", root)}>
      <CategoryDetailView
        {...props}
        entityType="class"
        headExtra={<ChainPlace root={root} categoryId={entityId} withLine />}
        extraSections={extraSections}
      />
    </HeaderExtraActionsContext.Provider>
  );
};

export default ClassDetailView;
